import React, { useEffect, useMemo, useRef, useState } from 'react'
import { httpsCallable } from 'firebase/functions'
import { functions } from '../../helpers/firebase'
import MixedContentPreview, { MathpixScope } from '../UiElements/MixedContentPreview'
import { redactPersonalInfo } from '../../helpers/contentSafety'
import { groupSentencesIntoBubbles } from '../../helpers/sentenceBubbles'
import { EXPLAIN_CHAT_MESSAGE_LIMIT } from '../../helpers/explainSessions'
import './ExplainAskModal.css'

// Mirrors EXPLAIN_MAX_ASK_MESSAGE_CHARS in functions/index.js: the callable
// rejects anything longer, so the textarea stops the student at the same point
// instead of letting them write a message that cannot be sent.
const MAX_MESSAGE_CHARS = 1000

const errorMessageFor = (error) => {
  const code = typeof error?.code === 'string'
    ? error.code.replace('functions/', '')
    : ''

  if (code === 'permission-denied') {
    return 'The AI chat helper is turned off for this account. Ask your parent if you need it back on.'
  }
  if (code === 'unauthenticated') {
    return 'Your session expired. Please sign in again.'
  }
  if (code === 'unavailable' || code === 'deadline-exceeded') {
    return 'We could not reach Quillnote. Check your connection and try again.'
  }
  if (code === 'resource-exhausted') {
    return 'This session has reached its chat limit. Finish it and start a new one.'
  }
  // invalid-argument / not-found / failed-precondition all carry a message
  // written for the student.
  if (code && typeof error?.message === 'string' && error.message.trim()) {
    return error.message
  }
  return 'Error communicating with AI. Please try again.'
}

const timestampFromMessage = (createdAt) => {
  if (!createdAt) return null
  if (createdAt instanceof Date) return createdAt
  if (typeof createdAt.toDate === 'function') return createdAt.toDate()
  if (typeof createdAt.toMillis === 'function') return new Date(createdAt.toMillis())
  if (typeof createdAt.seconds === 'number') return new Date(createdAt.seconds * 1000)
  return null
}

const formatMessageTime = (createdAt) => {
  const date = timestampFromMessage(createdAt)
  if (!date) return ''
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

// A long reply reads as a wall of text in one bubble, so an assistant message is
// shown as consecutive bubbles of at most two sentences. The student's own
// messages stay whole: they are one utterance the student chose to send.
const bubblesFor = (message) => {
  if (message.role !== 'assistant') return [message.content]
  const bubbles = groupSentencesIntoBubbles(message.content)
  return bubbles.length > 0 ? bubbles : [message.content]
}

// `actingStudentId` is set only in Student View, where the signed-in user is the
// parent. The callable honours it while the server-side Student View session
// names that child, so the tutor reply is written to the child's session.
const ExplainAskModal = ({
  sessionId,
  stepIndex,
  messages = [],
  actingStudentId = null,
  onClose,
  onMessagesAppend
}) => {
  const [inputMessage, setInputMessage] = useState('')
  const [isSending, setIsSending] = useState(false)
  const [pendingUserMessage, setPendingUserMessage] = useState(null)
  const [error, setError] = useState(null)
  const [isBlocked, setIsBlocked] = useState(false)
  const inputRef = useRef(null)
  const messagesEndRef = useRef(null)
  const isMountedRef = useRef(true)

  const atChatLimit = messages.length >= EXPLAIN_CHAT_MESSAGE_LIMIT - 1

  // One thread for the whole session, in send order, exactly as it is stored.
  // Grouping happens per message and never changes how many messages there are,
  // so `messages.length` still drives the chat limit.
  const visibleMessages = useMemo(() => {
    const thread = pendingUserMessage
      ? [...messages, pendingUserMessage]
      : messages
    return thread.map((message) => ({ ...message, bubbles: bubblesFor(message) }))
  }, [messages, pendingUserMessage])

  useEffect(() => {
    isMountedRef.current = true
    return () => { isMountedRef.current = false }
  }, [])

  useEffect(() => {
    inputRef.current?.focus()
  }, [])

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [visibleMessages, isSending])

  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [onClose])

  const handleSend = async () => {
    const trimmed = inputMessage.trim()
    if (!trimmed || isSending || isBlocked || atChatLimit) return

    setIsSending(true)
    setError(null)
    setPendingUserMessage({
      id: `pending-user-${Date.now()}`,
      role: 'user',
      content: redactPersonalInfo(trimmed),
      stepIndex,
      createdAt: new Date()
    })
    setInputMessage('')

    try {
      // Redacted here as well as on the server so nothing personal ever leaves
      // the browser, even in a request that later fails.
      const askExplainQuestion = httpsCallable(functions, 'askExplainQuestion')
      const result = await askExplainQuestion({
        ...(actingStudentId ? { studentId: actingStudentId } : {}),
        sessionId,
        message: redactPersonalInfo(trimmed),
        stepIndex
      })

      const content = result?.data?.content
      if (!result?.data?.success || typeof content !== 'string' || !content.trim()) {
        throw new Error('Empty tutor reply')
      }

      const sentAt = new Date()
      if (isMountedRef.current) {
        setPendingUserMessage(null)
      }
      onMessagesAppend([
        {
          id: `local-user-${sentAt.getTime()}`,
          role: 'user',
          content: redactPersonalInfo(trimmed),
          stepIndex,
          createdAt: sentAt
        },
        {
          id: `local-assistant-${sentAt.getTime() + 1}`,
          role: 'assistant',
          content: content.trim(),
          stepIndex,
          createdAt: new Date()
        }
      ], sessionId)
    } catch (askError) {
      console.error('Error asking explain tutor:', askError)
      if (isMountedRef.current) {
        const code = typeof askError?.code === 'string' ? askError.code : ''
        if (code.endsWith('permission-denied')) {
          setIsBlocked(true)
        }
        setError(errorMessageFor(askError))
        setInputMessage(trimmed)
      }
    } finally {
      if (isMountedRef.current) {
        setPendingUserMessage(null)
        setIsSending(false)
      }
    }
  }

  const handleKeyPress = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault()
      handleSend()
    }
  }

  const inputDisabled = isSending || isBlocked || atChatLimit

  return (
    <div className="explain-ask-overlay" onClick={onClose} role="presentation">
      <div
        className="explain-ask-modal"
        onClick={(event) => event.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="explain-ask-title"
      >
        <div className="explain-ask-header">
          <div className="explain-ask-header-left">
            <img src="/favicon.png" alt="" className="explain-ask-avatar" />
            <div>
              <h2 id="explain-ask-title">Ask about this problem</h2>
              <p className="explain-ask-subtitle">Your AI study buddy</p>
            </div>
          </div>
          <button
            type="button"
            className="explain-ask-close"
            onClick={onClose}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div className="explain-ask-messages">
          {/* One Mathpix stylesheet install for the whole thread, rather than
              one per bubble. */}
          <MathpixScope>
            {visibleMessages.map((message) => {
              const isAssistant = message.role === 'assistant'
              const sentAt = formatMessageTime(message.createdAt)
              return (
                <div
                  key={message.id}
                  className={`explain-ask-message ${isAssistant ? 'assistant-message' : 'user-message'}`}
                >
                  {message.bubbles.map((bubble, index) => (
                    <div className="explain-ask-message-row" key={`${message.id}-${index}`}>
                      {isAssistant && (index === 0 ? (
                        <img src="/favicon.png" alt="" className="explain-ask-message-avatar" />
                      ) : (
                        // Keeps the rest of a group aligned under the avatar
                        // without repeating it on every fragment.
                        <span className="explain-ask-message-avatar-offset" aria-hidden="true" />
                      ))}
                      <div className="explain-ask-message-content">
                        <MixedContentPreview content={bubble} />
                      </div>
                    </div>
                  ))}
                  {sentAt && (
                    <div className="explain-ask-message-meta">{sentAt}</div>
                  )}
                </div>
              )
            })}
            {isSending && (
              <div className="explain-ask-message assistant-message">
                <div className="explain-ask-message-row">
                  <img src="/favicon.png" alt="" className="explain-ask-message-avatar" />
                  <div className="explain-ask-message-content">
                    <div className="explain-ask-typing" aria-label="Tutor is typing">
                      <span></span>
                      <span></span>
                      <span></span>
                    </div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </MathpixScope>
        </div>

        {error && (
          <div className="explain-ask-error" role="alert">{error}</div>
        )}
        {atChatLimit && !error && (
          <div className="explain-ask-error" role="status">
            This session has reached its chat limit. Finish it and start a new one.
          </div>
        )}

        <div className="explain-ask-input-container">
          <textarea
            id="explain-ask-input"
            ref={inputRef}
            className="explain-ask-input"
            value={inputMessage}
            onChange={(event) => setInputMessage(event.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="Ask a question..."
            rows="3"
            maxLength={MAX_MESSAGE_CHARS}
            disabled={inputDisabled}
          />
          <button
            type="button"
            className="explain-ask-send-btn"
            onClick={handleSend}
            disabled={!inputMessage.trim() || inputDisabled}
          >
            Send
          </button>
        </div>
        <div className="explain-ask-meta">
          <span>{inputMessage.length}/{MAX_MESSAGE_CHARS}</span>
        </div>

        <div className="explain-ask-disclaimer">
          This is an AI chat helper. Remember that AI can make mistakes. Please double-check AI responses for accuracy.
        </div>
      </div>
    </div>
  )
}

export default ExplainAskModal
